import { getApiBaseUrl } from '../config/api';

/**
 * Creates an embedded PaymentIntent for a guide product.
 * productId: 'startup' | 'equipment' | 'bundle'
 * promoCode optional (validated server-side)
 * customerInfo optional at create time (attached before confirm).
 */
export async function createGuidePaymentIntent(productId, customerInfo, promoCode) {
  if (!productId) {
    throw new Error('A guide product is required to start checkout.');
  }

  const baseUrl = getApiBaseUrl();
  if (!baseUrl) {
    throw new Error('Checkout is not configured. Missing API base URL.');
  }

  const cleanBaseUrl = baseUrl.replace(/\/$/, '');
  const url = `${cleanBaseUrl}/payments/create-guide-checkout`;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      productId,
      ...(promoCode ? { promoCode } : {}),
      ...(customerInfo ? { customerInfo } : {}),
    }),
  });

  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    throw new Error(
      data?.error || `Unable to start checkout (${response.status}). Please try again.`
    );
  }

  if (!data?.clientSecret) {
    throw new Error('Payment session did not return a client secret.');
  }

  return data;
}

/**
 * Attaches name/email to an open guide PaymentIntent before confirm.
 */
export async function updateGuideCheckoutCustomer(paymentIntentId, customerInfo) {
  const baseUrl = getApiBaseUrl();
  if (!baseUrl) {
    throw new Error('API base URL is not configured.');
  }

  const cleanBaseUrl = baseUrl.replace(/\/$/, '');
  const url = `${cleanBaseUrl}/payments/update-guide-checkout-customer`;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ paymentIntentId, customerInfo }),
  });

  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    throw new Error(data?.error || 'Unable to save checkout details.');
  }

  return data;
}

/**
 * Loads signed download links for a paid PaymentIntent or Checkout Session.
 */
export async function fetchGuideDownloads({ paymentIntentId, sessionId } = {}) {
  const baseUrl = getApiBaseUrl();
  if (!baseUrl) {
    throw new Error('API base URL is not configured.');
  }

  const cleanBaseUrl = baseUrl.replace(/\/$/, '');
  const params = new URLSearchParams();
  if (paymentIntentId) params.set('payment_intent', paymentIntentId);
  if (sessionId) params.set('session_id', sessionId);

  if (![...params.keys()].length) {
    throw new Error('Missing payment reference.');
  }

  const url = `${cleanBaseUrl}/payments/guide-downloads?${params.toString()}`;
  const response = await fetch(url, { method: 'GET' });
  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    throw new Error(data?.error || `Unable to load downloads (${response.status}).`);
  }

  return data;
}
